import * as Cesium from "cesium";

export type MeasureMode = "none" | "distance" | "area";

export type MeasureResult = {
  label: string;
  detail?: string;
};

type AreaShape = {
  points: Cesium.Cartesian3[];
  closed: boolean;
  handles: Cesium.Entity[];
  outline: Cesium.Entity;
  fill: Cesium.Entity;
  label: Cesium.Entity;
};

function formatMeters(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m.toFixed(2)} m`;
}

function formatArea(m2: number): string {
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(3)} ha`;
  return `${m2.toFixed(2)} m²`;
}

function horizontalDistance(a: Cesium.Cartesian3, b: Cesium.Cartesian3): number {
  const cartoA = Cesium.Cartographic.fromCartesian(a);
  const cartoB = Cesium.Cartographic.fromCartesian(b);
  const geodesic = new Cesium.EllipsoidGeodesic(cartoA, cartoB);
  return geodesic.surfaceDistance;
}

function formatSignedMeters(m: number): string {
  const abs = formatMeters(Math.abs(m));
  if (m > 0.005) return `+${abs}`;
  if (m < -0.005) return `−${abs}`;
  return abs;
}

type HeightTriangle = {
  low: Cesium.Cartesian3;
  high: Cesium.Cartesian3;
  base: Cesium.Cartesian3;
  delta: number;
};

/** Right triangle: horizontal leg plus a vertical leg at the higher point. */
function heightTriangle(a: Cesium.Cartesian3, b: Cesium.Cartesian3): HeightTriangle {
  const cartoA = Cesium.Cartographic.fromCartesian(a);
  const cartoB = Cesium.Cartographic.fromCartesian(b);
  const bHigher = cartoB.height >= cartoA.height;
  const lowCarto = bHigher ? cartoA : cartoB;
  const highCarto = bHigher ? cartoB : cartoA;
  return {
    low: bHigher ? a : b,
    high: bHigher ? b : a,
    base: Cesium.Cartesian3.fromRadians(highCarto.longitude, highCarto.latitude, lowCarto.height),
    delta: cartoB.height - cartoA.height,
  };
}

function distanceDetail(a: Cesium.Cartesian3, b: Cesium.Cartesian3): string {
  const triangle = heightTriangle(a, b);
  return `3D distance\nHorizontal ${formatMeters(horizontalDistance(a, b))}\nHeight ${formatSignedMeters(triangle.delta)}`;
}

function polygonAreaHorizontal(positions: Cesium.Cartesian3[]): number {
  if (positions.length < 3) return 0;
  const origin = positions[0];
  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(origin);
  const inv = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
  const pts = positions.map((p) => {
    const local = Cesium.Matrix4.multiplyByPoint(inv, p, new Cesium.Cartesian3());
    return { x: local.x, y: local.y };
  });
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    sum += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(sum) / 2;
}

function centroid(positions: Cesium.Cartesian3[]): Cesium.Cartesian3 {
  const sum = new Cesium.Cartesian3();
  for (const p of positions) Cesium.Cartesian3.add(sum, p, sum);
  return Cesium.Cartesian3.multiplyByScalar(sum, 1 / positions.length, sum);
}

export class MeasureTool {
  private viewer: Cesium.Viewer;
  private handler: Cesium.ScreenSpaceEventHandler;
  private mode: MeasureMode = "none";
  private points: Cesium.Cartesian3[] = [];
  private entities: Cesium.Entity[] = [];
  private previewEntities: Cesium.Entity[] = [];
  private previewA = new Cesium.Cartesian3();
  private previewB = new Cesium.Cartesian3();
  private previewReady = false;
  private onResult: (result: MeasureResult | null) => void;

  private areas: AreaShape[] = [];
  private activeArea: AreaShape | null = null;
  private cursor: Cesium.Cartesian3 | null = null;
  private handles = new Map<Cesium.Entity, { shape: AreaShape; index: number }>();
  private drag: { shape: AreaShape; index: number } | null = null;
  private dragMoved = false;
  private suppressClick = false;
  private lastPointerUpAt = 0;
  private cameraLock: {
    enableInputs: boolean;
    enableRotate: boolean;
    enableTranslate: boolean;
    enableTilt: boolean;
    enableLook: boolean;
  } | null = null;

  constructor(viewer: Cesium.Viewer, onResult: (result: MeasureResult | null) => void) {
    this.viewer = viewer;
    this.onResult = onResult;
    this.handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    this.handler.setInputAction(this.onLeftDown, Cesium.ScreenSpaceEventType.LEFT_DOWN);
    this.handler.setInputAction(this.onLeftUp, Cesium.ScreenSpaceEventType.LEFT_UP);
    this.handler.setInputAction(this.onClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    this.handler.setInputAction(this.onDoubleClick, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    this.handler.setInputAction(this.onMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    this.handler.setInputAction(this.cancel, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
    viewer.scene.canvas.addEventListener("pointerdown", this.onPointerDown, true);
    viewer.scene.canvas.addEventListener("pointermove", this.onPointerMove, true);
    window.addEventListener("pointerup", this.onPointerUp, true);
  }

  setMode(mode: MeasureMode) {
    this.cancel();
    this.mode = mode;
    this.viewer.canvas.style.cursor = mode === "none" ? "default" : "crosshair";
    if (mode === "area") {
      this.onResult({
        label: "Area",
        detail: "Click corners on the model. Double-click to close.",
      });
    }
  }

  getMode() {
    return this.mode;
  }

  clearAll() {
    this.cancel();
    for (const shape of this.areas) this.removeShape(shape);
    this.areas = [];
    for (const e of this.entities) this.viewer.entities.remove(e);
    this.entities = [];
    this.onResult(null);
  }

  cancel = () => {
    this.endDrag();
    this.points = [];
    this.cursor = null;
    this.clearPreview();
    if (this.activeArea && !this.activeArea.closed) {
      this.removeShape(this.activeArea);
      this.areas = this.areas.filter((s) => s !== this.activeArea);
      this.activeArea = null;
    }
    if (this.mode === "area") {
      this.onResult({
        label: "Area",
        detail: "Click corners on the model. Double-click to close.",
      });
    } else if (this.mode === "distance") {
      this.onResult({
        label: "Distance",
        detail: "Click points on the model. The yellow line is the height difference.",
      });
    }
  };

  destroy() {
    this.viewer.scene.canvas.removeEventListener("pointerdown", this.onPointerDown, true);
    this.viewer.scene.canvas.removeEventListener("pointermove", this.onPointerMove, true);
    window.removeEventListener("pointerup", this.onPointerUp, true);
    this.handler.destroy();
    this.clearAll();
  }

  private pickPosition(windowPosition: Cesium.Cartesian2): Cesium.Cartesian3 | null {
    const scene = this.viewer.scene;
    const picked = scene.pick(windowPosition, 3, 3);
    if (!Cesium.defined(picked)) return null;
    if (scene.pickPositionSupported) {
      const pos = scene.pickPosition(windowPosition);
      if (
        Cesium.defined(pos) &&
        !Cesium.Cartesian3.equalsEpsilon(pos, Cesium.Cartesian3.ZERO, Cesium.Math.EPSILON8)
      ) {
        return pos;
      }
    }
    return null;
  }

  private eventPosition(ev: PointerEvent): Cesium.Cartesian2 {
    const rect = this.viewer.canvas.getBoundingClientRect();
    return new Cesium.Cartesian2(ev.clientX - rect.left, ev.clientY - rect.top);
  }

  private handleAt(windowPosition: Cesium.Cartesian2) {
    const hits = this.viewer.scene.drillPick(windowPosition, 12, 18, 18);
    for (const hit of hits) {
      const entity = hit?.id as Cesium.Entity | undefined;
      if (!entity) continue;
      const handle = this.handles.get(entity);
      if (handle) return handle;
    }
    return null;
  }

  private onPointerDown = (ev: PointerEvent) => {
    if (this.mode !== "area" || ev.button !== 0 || this.drag) return;
    const handle = this.handleAt(this.eventPosition(ev));
    if (!handle) return;
    // Runs before Cesium's camera listener, so the view stays put while a corner moves.
    this.beginDrag(handle);
    ev.preventDefault();
    ev.stopPropagation();
  };

  private onPointerMove = (ev: PointerEvent) => {
    if (!this.drag) return;
    this.moveDrag(this.eventPosition(ev));
    ev.preventDefault();
    ev.stopPropagation();
  };

  private onPointerUp = (ev: PointerEvent) => {
    const now = performance.now();
    const elapsed = now - this.lastPointerUpAt;
    this.lastPointerUpAt = now;
    if (!this.drag) return;
    const shape = this.drag.shape;
    const index = this.drag.index;
    const moved = this.dragMoved;
    this.endDrag();
    if (!moved && !shape.closed && shape.points.length >= 3) {
      const quickClose = elapsed < 400 && index === shape.points.length - 1;
      if (index === 0 || quickClose) this.closeArea(shape);
    } else if (moved && shape.closed) {
      this.publishClosed(shape);
    }
    ev.preventDefault();
    ev.stopPropagation();
  };

  private onLeftDown = (movement: { position: Cesium.Cartesian2 }) => {
    if (this.mode !== "area" || this.drag) return;
    const handle = this.handleAt(movement.position);
    if (!handle) return;
    this.beginDrag(handle);
  };

  private onLeftUp = () => {
    if (!this.drag) return;
    const shape = this.drag.shape;
    const moved = this.dragMoved;
    this.endDrag();
    if (moved && shape.closed) this.publishClosed(shape);
  };

  private beginDrag(handle: { shape: AreaShape; index: number }) {
    if (this.drag) return;
    this.drag = handle;
    this.dragMoved = false;
    this.suppressClick = true;
    this.cursor = null;
    const controller = this.viewer.scene.screenSpaceCameraController;
    this.cameraLock = {
      enableInputs: controller.enableInputs,
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
    };
    controller.enableInputs = false;
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableTilt = false;
    controller.enableLook = false;
    this.viewer.canvas.style.cursor = "grabbing";
  }

  private moveDrag(windowPosition: Cesium.Cartesian2) {
    if (!this.drag) return;
    const pos = this.pickModel(windowPosition);
    if (!pos) return;
    this.dragMoved = true;
    Cesium.Cartesian3.clone(pos, this.drag.shape.points[this.drag.index]);
    const shape = this.drag.shape;
    if (shape.closed) {
      this.publishClosed(shape);
    } else if (shape.points.length >= 3) {
      this.onResult({
        label: formatArea(polygonAreaHorizontal(shape.points)),
        detail: "Double-click, or click the first corner, to close.",
      });
    }
  }

  private endDrag() {
    if (!this.drag) return;
    this.drag = null;
    const controller = this.viewer.scene.screenSpaceCameraController;
    const lock = this.cameraLock;
    this.cameraLock = null;
    if (lock) {
      controller.enableInputs = lock.enableInputs;
      controller.enableRotate = lock.enableRotate;
      controller.enableTranslate = lock.enableTranslate;
      controller.enableTilt = lock.enableTilt;
      controller.enableLook = lock.enableLook;
    }
    this.viewer.canvas.style.cursor = this.mode === "none" ? "default" : "crosshair";
  }

  private pickModel(windowPosition: Cesium.Cartesian2): Cesium.Cartesian3 | null {
    const hidden: Cesium.Entity[] = [];
    const hide = (entity?: Cesium.Entity | null) => {
      if (!entity || entity.show === false) return;
      entity.show = false;
      hidden.push(entity);
    };
    for (const entity of this.entities) hide(entity);
    for (const entity of this.previewEntities) hide(entity);
    for (const shape of this.areas) {
      hide(shape.outline);
      hide(shape.fill);
      hide(shape.label);
      for (const handle of shape.handles) hide(handle);
    }
    try {
      this.viewer.scene.render();
      const scene = this.viewer.scene;
      if (!scene.pickPositionSupported) return null;
      const picked = scene.pick(windowPosition, 5, 5);
      if (!Cesium.defined(picked)) return null;
      const pos = scene.pickPosition(windowPosition);
      if (
        !Cesium.defined(pos) ||
        Cesium.Cartesian3.equalsEpsilon(pos, Cesium.Cartesian3.ZERO, Cesium.Math.EPSILON8)
      ) {
        return null;
      }
      return Cesium.Cartesian3.clone(pos);
    } finally {
      for (const entity of hidden) entity.show = true;
      this.viewer.scene.render();
    }
  }

  private onClick = (movement: { position: Cesium.Cartesian2 }) => {
    if (this.mode === "none") return;
    if (this.suppressClick) {
      this.suppressClick = false;
      if (
        !this.dragMoved &&
        this.activeArea &&
        !this.activeArea.closed &&
        this.activeArea.points.length >= 3
      ) {
        const handle = this.handleAt(movement.position);
        if (handle?.shape === this.activeArea && handle.index === 0) {
          this.closeArea(this.activeArea);
        }
      }
      this.dragMoved = false;
      return;
    }
    if (this.mode === "area") {
      this.addAreaCorner(movement.position);
      return;
    }

    const pos = this.pickPosition(movement.position);
    if (!pos) return;
    this.clearPreview();
    this.points.push(Cesium.Cartesian3.clone(pos));
    this.addPointEntity(pos);
    if (this.points.length >= 2) {
      const a = this.points[this.points.length - 2];
      const b = this.points[this.points.length - 1];
      this.addDistanceSegment(a, b);
      this.onResult({
        label: formatMeters(Cesium.Cartesian3.distance(a, b)),
        detail: distanceDetail(a, b),
      });
    }
  };

  private onDoubleClick = () => {
    if (this.mode !== "area" || !this.activeArea || this.activeArea.closed) return;
    const shape = this.activeArea;
    if (shape.points.length >= 2) {
      const last = shape.points[shape.points.length - 1];
      const prev = shape.points[shape.points.length - 2];
      if (Cesium.Cartesian3.distance(last, prev) < 2) {
        this.popCorner(shape);
      }
    }
    if (shape.points.length >= 3) this.closeArea(shape);
    else {
      this.onResult({
        label: "Area",
        detail: "Add at least 3 corners, then double-click to close.",
      });
    }
  };

  private addAreaCorner(windowPosition: Cesium.Cartesian2) {
    const shape = this.ensureActiveArea();
    if (shape.points.length >= 3) {
      const first = this.toScreen(shape.points[0]);
      if (first && Cesium.Cartesian2.distance(first, windowPosition) < 16) {
        this.closeArea(shape);
        return;
      }
    }
    const pos = this.pickPosition(windowPosition);
    if (!pos) return;
    shape.points.push(Cesium.Cartesian3.clone(pos));
    this.addHandle(shape, shape.points.length - 1);
    shape.fill.show = shape.points.length >= 3;
    const count = shape.points.length;
    if (count < 3) {
      this.onResult({
        label: "Area",
        detail:
          count === 1
            ? "Click to add the next corner."
            : "Click to add corners. Double-click to close.",
      });
    } else {
      this.onResult({
        label: formatArea(polygonAreaHorizontal(shape.points)),
        detail: "Double-click, or click the first corner, to close.",
      });
    }
  }

  private closeArea(shape: AreaShape) {
    if (shape.closed || shape.points.length < 3) return;
    shape.closed = true;
    this.cursor = null;
    this.activeArea = null;
    for (const handle of shape.handles) {
      if (handle.point) {
        handle.point.pixelSize = new Cesium.ConstantProperty(12);
        handle.point.color = new Cesium.ConstantProperty(Cesium.Color.CYAN);
      }
    }
    shape.label.show = true;
    this.publishClosed(shape);
    this.viewer.canvas.style.cursor = "crosshair";
  }

  private publishClosed(shape: AreaShape) {
    this.onResult({
      label: formatArea(polygonAreaHorizontal(shape.points)),
      detail: `Closed · ${shape.points.length} corners · drag a corner to adjust`,
    });
  }

  private ensureActiveArea(): AreaShape {
    if (this.activeArea && !this.activeArea.closed) return this.activeArea;
    const shape: AreaShape = {
      points: [],
      closed: false,
      handles: [],
      outline: undefined as unknown as Cesium.Entity,
      fill: undefined as unknown as Cesium.Entity,
      label: undefined as unknown as Cesium.Entity,
    };
    shape.outline = this.viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => this.outlinePositions(shape), false),
        width: 3,
        material: Cesium.Color.fromCssColorString("#38bdf8"),
        depthFailMaterial: Cesium.Color.fromCssColorString("#38bdf8").withAlpha(0.45),
      },
    });
    shape.fill = this.viewer.entities.add({
      polygon: {
        hierarchy: new Cesium.CallbackProperty(() => {
          const ring = this.fillPositions(shape);
          return new Cesium.PolygonHierarchy(ring.length >= 3 ? ring : shape.points);
        }, false),
        material: Cesium.Color.fromCssColorString("#38bdf8").withAlpha(0.28),
        perPositionHeight: true,
        outline: false,
      },
      show: false,
    });
    shape.label = this.viewer.entities.add({
      position: new Cesium.CallbackPositionProperty(
        () => (shape.points.length ? centroid(shape.points) : new Cesium.Cartesian3()),
        false,
      ),
      show: false,
      label: {
        text: new Cesium.CallbackProperty(
          () => formatArea(polygonAreaHorizontal(shape.points)),
          false,
        ),
        font: "600 15px Segoe UI, sans-serif",
        fillColor: Cesium.Color.WHITE,
        showBackground: true,
        backgroundColor: Cesium.Color.fromCssColorString("#0b1220").withAlpha(0.88),
        backgroundPadding: new Cesium.Cartesian2(8, 6),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        pixelOffset: new Cesium.Cartesian2(0, -18),
      },
    });
    this.areas.push(shape);
    this.activeArea = shape;
    return shape;
  }

  private fillPositions(shape: AreaShape): Cesium.Cartesian3[] {
    if (shape.closed || !this.cursor || this.activeArea !== shape) return shape.points;
    return [...shape.points, this.cursor];
  }

  private outlinePositions(shape: AreaShape): Cesium.Cartesian3[] {
    const ring = this.fillPositions(shape);
    if (ring.length < 2) return ring;
    if (ring.length >= 3) return [...ring, ring[0]];
    return ring;
  }

  private addHandle(shape: AreaShape, index: number) {
    const isFirst = index === 0 && !shape.closed;
    const entity = this.viewer.entities.add({
      position: new Cesium.CallbackPositionProperty(() => shape.points[index], false),
      point: {
        pixelSize: isFirst ? 16 : 11,
        color: isFirst ? Cesium.Color.WHITE : Cesium.Color.CYAN,
        outlineColor: Cesium.Color.fromCssColorString("#38bdf8"),
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
    shape.handles.push(entity);
    this.handles.set(entity, { shape, index });
  }

  private popCorner(shape: AreaShape) {
    const handle = shape.handles.pop();
    shape.points.pop();
    if (handle) {
      this.handles.delete(handle);
      this.viewer.entities.remove(handle);
    }
  }

  private removeShape(shape: AreaShape) {
    for (const handle of shape.handles) {
      this.handles.delete(handle);
      this.viewer.entities.remove(handle);
    }
    this.viewer.entities.remove(shape.outline);
    this.viewer.entities.remove(shape.fill);
    this.viewer.entities.remove(shape.label);
  }

  private toScreen(position: Cesium.Cartesian3): Cesium.Cartesian2 | undefined {
    return Cesium.SceneTransforms.worldToWindowCoordinates(this.viewer.scene, position);
  }

  private onMove = (movement: { endPosition: Cesium.Cartesian2 }) => {
    if (this.drag) return;

    if (this.mode === "area" && this.activeArea && !this.activeArea.closed) {
      const overFirst =
        this.activeArea.points.length >= 3 &&
        this.handleAt(movement.endPosition)?.index === 0 &&
        this.handleAt(movement.endPosition)?.shape === this.activeArea;
      this.viewer.canvas.style.cursor = overFirst ? "pointer" : "crosshair";
      const pos = this.pickPosition(movement.endPosition);
      this.cursor = pos ? Cesium.Cartesian3.clone(pos) : null;
      this.activeArea.fill.show = this.fillPositions(this.activeArea).length >= 3;
      if (this.activeArea.points.length >= 2 && this.cursor) {
        const ring = [...this.activeArea.points, this.cursor];
        this.onResult({
          label:
            ring.length >= 3
              ? formatArea(polygonAreaHorizontal(ring))
              : "Area",
          detail: overFirst
            ? "Click the first corner to close."
            : "Double-click to close the area.",
        });
      }
      return;
    }

    if (this.mode === "none" || this.points.length === 0 || this.mode !== "distance") return;
    const pos = this.pickPosition(movement.endPosition);
    if (!pos) return;
    const last = this.points[this.points.length - 1];
    this.showDistancePreview(last, pos);
    this.onResult({
      label: formatMeters(Cesium.Cartesian3.distance(last, pos)),
      detail: distanceDetail(last, pos),
    });
  };

  private showDistancePreview(a: Cesium.Cartesian3, b: Cesium.Cartesian3) {
    Cesium.Cartesian3.clone(a, this.previewA);
    Cesium.Cartesian3.clone(b, this.previewB);
    this.previewReady = true;
    if (this.previewEntities.length === 0) this.createDistancePreview();
    for (const entity of this.previewEntities) entity.show = true;
  }

  private createDistancePreview() {
    const straight = new Cesium.CallbackProperty(() => {
      if (!this.previewReady) return [];
      return [this.previewA, this.previewB];
    }, false);
    const horizontal = new Cesium.CallbackProperty(() => {
      if (!this.previewReady) return [];
      const triangle = heightTriangle(this.previewA, this.previewB);
      return [triangle.low, triangle.base];
    }, false);
    const vertical = new Cesium.CallbackProperty(() => {
      if (!this.previewReady) return [];
      const triangle = heightTriangle(this.previewA, this.previewB);
      return [triangle.base, triangle.high];
    }, false);
    this.previewEntities = [
      this.lineEntity(straight, "#38bdf8", 2, false),
      this.lineEntity(horizontal, "#7dd3fc", 2, true),
      this.lineEntity(vertical, "#fbbf24", 4, false),
      this.viewer.entities.add({
        position: new Cesium.CallbackPositionProperty(
          () => Cesium.Cartesian3.midpoint(this.previewA, this.previewB, new Cesium.Cartesian3()),
          false,
        ),
        label: this.distanceLabelGraphics(
          new Cesium.CallbackProperty(
            () => formatMeters(Cesium.Cartesian3.distance(this.previewA, this.previewB)),
            false,
          ),
        ),
      }),
      this.viewer.entities.add({
        position: new Cesium.CallbackPositionProperty(() => {
          const triangle = heightTriangle(this.previewA, this.previewB);
          return Cesium.Cartesian3.midpoint(triangle.base, triangle.high, new Cesium.Cartesian3());
        }, false),
        label: this.heightLabelGraphics(
          new Cesium.CallbackProperty(
            () => `Height ${formatSignedMeters(heightTriangle(this.previewA, this.previewB).delta)}`,
            false,
          ),
        ),
      }),
    ];
  }

  private clearPreview() {
    this.previewReady = false;
    for (const entity of this.previewEntities) this.viewer.entities.remove(entity);
    this.previewEntities = [];
  }

  private addDistanceSegment(a: Cesium.Cartesian3, b: Cesium.Cartesian3) {
    const triangle = heightTriangle(a, b);
    this.entities.push(this.lineEntity([a, b], "#38bdf8", 3, false));
    this.entities.push(this.lineEntity([triangle.low, triangle.base], "#7dd3fc", 2, true));
    this.entities.push(this.lineEntity([triangle.base, triangle.high], "#fbbf24", 4, false));
    const segmentMid = Cesium.Cartesian3.midpoint(a, b, new Cesium.Cartesian3());
    this.entities.push(
      this.viewer.entities.add({
        position: segmentMid,
        label: this.distanceLabelGraphics(formatMeters(Cesium.Cartesian3.distance(a, b))),
      }),
    );
    const heightMid = Cesium.Cartesian3.midpoint(triangle.base, triangle.high, new Cesium.Cartesian3());
    this.entities.push(
      this.viewer.entities.add({
        position: heightMid,
        label: this.heightLabelGraphics(`Height ${formatSignedMeters(triangle.delta)}`),
      }),
    );
  }

  private distanceLabelGraphics(text: string | Cesium.Property) {
    return {
      text,
      font: "600 14px Segoe UI, sans-serif",
      fillColor: Cesium.Color.WHITE,
      showBackground: true,
      backgroundColor: Cesium.Color.fromCssColorString("#0b1220").withAlpha(0.9),
      backgroundPadding: new Cesium.Cartesian2(8, 5),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      pixelOffset: new Cesium.Cartesian2(0, -14),
      horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
      verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
    };
  }

  private heightLabelGraphics(text: string | Cesium.Property) {
    return {
      text,
      font: "600 14px Segoe UI, sans-serif",
      fillColor: Cesium.Color.fromCssColorString("#fde68a"),
      showBackground: true,
      backgroundColor: Cesium.Color.fromCssColorString("#422006").withAlpha(0.9),
      backgroundPadding: new Cesium.Cartesian2(8, 5),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      pixelOffset: new Cesium.Cartesian2(12, 0),
      horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
      verticalOrigin: Cesium.VerticalOrigin.CENTER,
    };
  }

  private lineEntity(
    positions: Cesium.Cartesian3[] | Cesium.CallbackProperty,
    color: string,
    width: number,
    dashed: boolean,
  ) {
    const paint = Cesium.Color.fromCssColorString(color);
    const material = dashed ? new Cesium.PolylineDashMaterialProperty({ color: paint }) : paint;
    const depthFail = dashed
      ? new Cesium.PolylineDashMaterialProperty({ color: paint.withAlpha(0.55) })
      : paint.withAlpha(0.55);
    return this.viewer.entities.add({
      polyline: {
        positions,
        width,
        material,
        depthFailMaterial: depthFail,
      },
    });
  }

  private addPointEntity(pos: Cesium.Cartesian3) {
    const e = this.viewer.entities.add({
      position: pos,
      point: {
        pixelSize: 10,
        color: Cesium.Color.CYAN,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
    this.entities.push(e);
  }

}
